/** Exception-first dashboard (ARCHITECTURE §11, PRD §3.5/§57): what needs the owner first, then the pipeline at a glance. */
import { Link } from "../../app/router";
import type { AttentionItem, Dashboard } from "../../api/types";
import { Icon, type IconName } from "../../components/Icon";
import { Card, Dot, EmptyState, QueryView } from "../../components/ui";
import { UsageGauge } from "../../components/UsageGauge";
import { useDashboard } from "../../data/queries";
import { dateTime, relative } from "../../lib/format";
import { runnerHealth, type Tone } from "../../lib/status";
import { PauseBanner } from "../publish/PublishingPause";

export const isPublicationKind = (k: AttentionItem["kind"]) => k.startsWith("publication_");

function hrefFor(item: AttentionItem): string {
  if (item.kind === "decision") return item.series_id ? `/studio/${item.series_id}` : "/studio?filter=attention";
  if (item.kind === "publishing_paused") return "/settings#publishing";
  if (isPublicationKind(item.kind)) return `/publish?tab=attention&focus=${encodeURIComponent(item.id)}`;
  if (item.kind === "account_auth") return "/settings/accounts";
  if (item.kind === "learning_proposed") return "/learnings";
  if (item.version_id) return `/review/${item.version_id}`;
  return item.series_id ? `/studio/${item.series_id}` : "/";
}
const ATTENTION_ICON: Record<AttentionItem["kind"], IconName> = {
  decision: "message", publication_failed: "alert", publication_auth_required: "lock", publication_slot_missed: "clock", publication_blocked: "pause", publication_not_live: "alert",
  publication_waiting: "clock", publishing_paused: "pause", work_item_escalated: "flag", account_auth: "lock", learning_proposed: "bulb", other: "alert",
};
const ATTENTION_TONE: Partial<Record<AttentionItem["kind"], "attention" | "danger">> = { decision: "attention", learning_proposed: "attention", publication_waiting: "attention", publishing_paused: "attention", publication_blocked: "attention" };

export function HomeScreen() {
  const q = useDashboard();
  return (
    <div className="stack-lg">
      <QueryView q={q}>{(d) => <HomeBody d={d} />}</QueryView>
    </div>
  );
}

function HomeBody({ d }: { d: Dashboard }) {
  const n = d.needs_attention.total;
  const health = runnerHealth(d.runner?.last_seen_at);
  const learnings = d.learnings_pending;
  const tiles: { label: string; value: number; to: string; tone: Tone; icon: IconName; hint?: string; hintTo?: string }[] = [
    { label: "Needs attention", value: n, to: "/studio?filter=attention", tone: n ? "attention" : "ok", icon: "alert", hint: learnings ? `${learnings} learning${learnings === 1 ? "" : "s"} to review` : undefined, hintTo: "/learnings" },
    { label: "Awaiting review", value: d.awaiting_review, to: "/studio?filter=in_review", tone: d.awaiting_review ? "attention" : "neutral", icon: "play" },
    { label: "Plans awaiting selection", value: d.plans_awaiting_selection, to: "/studio?filter=awaiting_selection", tone: d.plans_awaiting_selection ? "attention" : "neutral", icon: "check" },
    { label: "Generating", value: d.generating, to: "/studio?filter=building", tone: "info", icon: "sparkle" },
    { label: "Scheduled · next 7 days", value: d.scheduled_7d, to: "/publish", tone: "neutral", icon: "calendar" },
    { label: "Published today", value: d.published_today, to: "/publish?tab=published", tone: d.published_today ? "ok" : "neutral", icon: "publish" },
    { label: "Comments needing attention", value: d.comments_needing_attention, to: "/inbox", tone: d.comments_needing_attention ? "attention" : "neutral", icon: "inbox" },
    { label: "Learnings to review", value: learnings, to: "/learnings", tone: learnings ? "attention" : "neutral", icon: "bulb" },
  ];
  const na = d.needs_attention;
  const pubIssues = na.items.filter((i) => isPublicationKind(i.kind)).length;
  if (pubIssues) tiles.splice(4, 0, { label: "Posts needing attention", value: pubIssues, to: "/publish?tab=attention", tone: "danger", icon: "alert" });
  // M2.1 hardening: held posts, posts that did not go live / are no longer public, long waits (shown only when non-zero).
  const extra = ([
    { label: "Posts on hold", value: na.blocked_publications, to: "/publish?tab=attention", tone: "danger", icon: "pause" },
    { label: "Did not go live", value: na.not_live_publications, to: "/publish?tab=attention", tone: "danger", icon: "alert" },
    { label: "Waiting a long time", value: na.long_waiting_publications, to: "/publish?tab=history&state=retry_pending,rate_limited", tone: "attention", icon: "clock" },
    { label: "Checks after restore", value: na.pending_dedupe_checks, to: "/settings#publishing", tone: "attention", icon: "refresh" },
  ] satisfies typeof tiles).filter((t) => t.value > 0);
  tiles.splice(5, 0, ...extra);
  return (
    <>
      <PauseBanner />
      <header className={`hero tone-${n ? "attention" : "ok"}`}>
        <span className="hero-icon"><Icon name={n ? "alert" : "check"} size={26} /></span>
        <div>
          <h1>{n ? `${n} ${n === 1 ? "item needs" : "items need"} your attention` : "Everything healthy"}</h1>
          <p className="muted">{n ? "Answer these first — everything else is running on its own." : "Nothing is waiting on you. The Studio and the scheduler are running."}</p>
        </div>
      </header>

      {n > 0 && (
        <Card title="Needs your attention" tone="attention">
          <ul className="list">
            {d.needs_attention.items.map((it) => (
              <li key={`${it.kind}-${it.id}`}>
                <Link to={hrefFor(it)} className="list-row">
                  <span className={`list-icon tone-${ATTENTION_TONE[it.kind] ?? "danger"}`}><Icon name={ATTENTION_ICON[it.kind]} size={20} /></span>
                  <span className="list-main">
                    <span className="list-title">{it.title}</span>
                    {(it.detail || it.at) && <span className="list-sub">{[it.detail, it.at ? relative(it.at) : null].filter(Boolean).join(" · ")}</span>}
                  </span>
                  <Icon name="chevronRight" className="list-chevron" />
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <section aria-label="Pipeline at a glance" className="tiles">
        {tiles.map((t) => (
          <div key={t.label} className={`tile tone-${t.tone}`}>
            <Link to={t.to} className="tile-link">
              <span className="tile-top"><Icon name={t.icon} size={18} /><span className="tile-label">{t.label}</span></span>
              <span className="tile-value">{t.value}</span>
            </Link>
            {t.hint && <Link to={t.hintTo ?? t.to} className="tile-hint">{t.hint}</Link>}
          </div>
        ))}
        <Link to="/settings#runner" className={`tile tile-runner tone-${health.tone}`}>
          <span className="tile-top"><Dot tone={health.tone} label={health.label} /><span className="tile-label">{health.label}</span></span>
          {d.runner ? (
            <span className="tile-runner-body">
              <UsageGauge label="5h" value={d.runner.usage?.five_hour_pct ?? null} />
              <UsageGauge label="7d" value={d.runner.usage?.seven_day_pct ?? null} />
              <span className="muted small">{d.runner.usage?.resets_at ? `resets ${dateTime(d.runner.usage.resets_at)}` : `seen ${relative(d.runner.last_seen_at)}`}</span>
            </span>
          ) : <span className="muted small">No runner has connected yet.</span>}
        </Link>
      </section>

      <Card title="Inventory runway" action={<Link to="/settings/queues" className="text-link">Queues</Link>}>
        {d.runway.length ? (
          <ul className="runway">
            {d.runway.map((r) => (
              <li key={r.queue_id} className={r.days != null && r.days < 3 ? "tone-attention" : ""}>
                <span>{r.name}</span>
                <span className="muted">{r.scheduled} scheduled{r.inventory != null ? ` · ${r.inventory} ready` : ""}{r.days != null ? ` · ~${r.days} day${r.days === 1 ? "" : "s"}` : ""}</span>
              </li>
            ))}
          </ul>
        ) : <EmptyState icon="calendar" title="No queues yet" action={<Link to="/settings/queues" className="btn btn-secondary btn-md">Create a queue</Link>}>A queue posts one category in one language at set times.</EmptyState>}
      </Card>
    </>
  );
}
